// Right pane: Results (Guideline and Proposed panels), Cash Flow, Notes and Help.
import type { CaseResult, PartyIndex, Tactic } from '@divorceos/schema';
import { act } from './actions';
import { useCaseView, type CaseView } from './caseView';
import { GLOSSARY, KEYS, LEG, helpFor } from './help';
import {
  TACTICS, amt, ageAt, fcWord, money, other, paren, payorOfSigned, signed, ssName, subLetter, tacticLong, tacticUsesSub,
} from './model';
import { rowLabel, SUB_PARENT } from './rows';
import { useDis, type RightTab } from './store';
import { useState } from 'react';
import { ReleasePicker } from './ReleasePicker';

const TABS: [RightTab, string, string][] = [['results', 'Results', 'R'], ['cash', 'Cash Flow', 'F'], ['notes', 'Notes', 'N'], ['help', 'Help', 'H']];

export function RightPane() {
  const v = useCaseView();
  const right = useDis((s) => s.ui.right);
  const setUi = useDis((s) => s.setUi);
  const ch = v.changed.length;
  return (
    <>
      <div className="subtabs" role="tablist">
        {TABS.map(([id, l, k]) => <button key={id} type="button" role="tab" className={right === id ? 'on' : ''} aria-selected={right === id} onClick={() => setUi({ right: id, helpTopic: null })}>{l}<span className="k">{k}</span></button>)}
        <span className="sp" />
        <button type="button" className={`lnk${ch ? ' chg' : ''}`} onClick={() => act('settings')}>{ch ? `${ch} ${ch === 1 ? 'setting' : 'settings'} changed` : 'Default settings'}</button>
      </div>
      <div className="pbody" id="rightBody">
        <div className="rbody">
          {right === 'results' && <Results />}
          {right === 'cash' && <CashFlow />}
          {right === 'notes' && <Notes />}
          {right === 'help' && <Help />}
        </div>
      </div>
    </>
  );
}

function Who({ p, labels }: { p: PartyIndex; labels: [string, string] }) {
  return <span className={`who ${p === 0 ? 'F' : 'M'}`}>{labels[p]}</span>;
}

function Headline({ total, payor, v }: { total: number; payor: PartyIndex | null; v: CaseView }) {
  if (payor === null || Math.round(total) === 0) return <div className="headline">No payment</div>;
  return <div className="headline"><Who p={payor} labels={v.labels} /> pays <Who p={other(payor)} labels={v.labels} /> <b>{money(v.c, total)}</b> <span className="muted" style={{ fontSize: 13 }}>{v.c.mode === 'ANNUAL' ? 'a year' : 'a month'}</span></div>;
}

export function Fc4055Notice() {
  const v = useCaseView();
  const toggle = <button type="button" className="lnkbtn" onClick={() => act('r:help:sb343')}>Why</button>;
  if (v.fcResolved !== v.fcAuto) {
    return <div className="notice amb"><span>⚠</span><span><b>FC 4055 {fcWord(v.fcResolved)} 1 Sep 2024 is in use</b>, which differs from what the tax year suggests. Check that this is intended. {toggle}</span></div>;
  }
  if (v.c.taxYear === 2024) {
    return <div className="notice amb"><span>⚠</span><span><b>Tax year 2024 straddles the 1 Sep 2024 change.</b> Automatic uses FC 4055 {fcWord(v.fcResolved)} 1 Sep 2024. Press <kbd>G</kbd> to compare with the other version. {toggle}</span></div>;
  }
  if (v.fcApplied === 'SB343') return <div className="notice"><span>ℹ</span><span><b>Guideline formula from 1 Sep 2024</b> (SB 343): revised income bands, a low-income adjustment based on the minimum wage, and add-ons shared by net income.</span></div>;
  return <div className="notice"><span>ℹ</span><span>This case uses <b>FC 4055 as it read before 1 Sep 2024</b> (tax year {v.c.taxYear}). Press <kbd>G</kbd> to compare with the amended formula.</span></div>;
}

interface Panel { cs: number; ad: number; ss: number; csBy: PartyIndex | null; adBy: PartyIndex | null; ssBy: PartyIndex | null; total: number; payor: PartyIndex | null }

export function guidelinePanel(r: CaseResult): Panel {
  const g = r.guideline;
  return { cs: g.basicCs.amount, ad: g.addOns.amount, ss: g.ss.amount, csBy: g.basicCs.payor, adBy: g.addOns.payor, ssBy: g.ss.payor, total: g.total.amount, payor: g.total.payor };
}
export function proposedPanel(r: CaseResult): Panel {
  const p = r.proposed;
  const sum = signed(p.nondeductible) + signed(p.addOns) + signed(p.deductible);
  return { cs: p.nondeductible.amount, ad: p.addOns.amount, ss: p.deductible.amount, csBy: p.nondeductible.payor, adBy: p.addOns.payor, ssBy: p.deductible.payor, total: Math.abs(sum), payor: payorOfSigned(sum) };
}

/** "Santa Clara SS (adjusted, nondeductible)". */
export function ssLineLabel(v: CaseView): string {
  const s = v.c.settings;
  const base = s.ssMethod === 'USER' ? 'User spousal support' : s.ssMethod === 'FORMULA' ? 'User formula SS' : `${ssName(s.ssMethod)} SS`;
  return `${base} (${s.ssOnAdjustedNets ? 'adjusted' : 'unadjusted'}, ${deductLabel(v)})`;
}
export function deductLabel(v: CaseView): string {
  const s = v.c.settings;
  return s.domesticPartnership ? 'deductible CA only' : s.includeSsAfter2018InFederal ? 'deductible' : 'nondeductible';
}

function Lines({ kind, panel }: { kind: 'g' | 'p'; panel: Panel }) {
  const v = useCaseView();
  const open = useDis((s) => (kind === 'g' ? s.ui.gOpen : s.ui.pOpen));
  const setUi = useDis((s) => s.setUi);
  const r = v.result;
  if (!r) return null;
  const L = v.labels;
  const presumedSigned = (panel.csBy === 1 ? -panel.cs : panel.cs) + (panel.adBy === 1 ? -panel.ad : panel.adBy === 0 ? panel.ad : 0);
  const presBy = payorOfSigned(presumedSigned) ?? panel.csBy;
  const py = (p: PartyIndex | null, n: number) => (p !== null && Math.round(n) ? `payor ${L[p]}` : '');
  const hid = open ? '' : ' hid';
  return (
    <>
      <tr><td><button type="button" className="tri" aria-expanded={open} onClick={() => setUi(kind === 'g' ? { gOpen: !open } : { pOpen: !open })}>{open ? '▼' : '▶'}</button>Presumed child support</td><td className="py">{py(presBy, presumedSigned)}</td><td className="n">{amt(v.c, presumedSigned)}</td></tr>
      <tr className={`sub${hid}`}><td>Basic child support{v.c.settings.csMethod === 'USER' ? ' (user amount)' : ''}</td><td className="py">{panel.csBy !== presBy ? py(panel.csBy, panel.cs) : ''}</td><td className="n">{amt(v.c, panel.cs)}</td></tr>
      <tr className={`sub${hid}`}><td>Add-ons ({r.guideline.cceAllocationLabel})</td><td className="py">{panel.adBy !== presBy ? py(panel.adBy, panel.ad) : ''}</td><td className="n">{amt(v.c, panel.ad)}</td></tr>
      {kind === 'g' && r.perChild.map((pc, i) => (
        <tr key={i} className={`sub2${hid}`}><td>{pc.name || `Child ${i + 1}`}{pc.dateOfBirth ? ` (${ageAt(pc.dateOfBirth, v.c.taxYear)})` : ''}</td><td className="py">{pc.presumedCs.payor !== presBy ? py(pc.presumedCs.payor, pc.presumedCs.amount) : ''}</td><td className="n">{amt(v.c, pc.presumedCs.amount)}</td></tr>
      ))}
      <tr><td>{ssLineLabel(v)}</td><td className="py">{py(panel.ssBy, panel.ss)}</td><td className="n">{amt(v.c, panel.ss)}</td></tr>
      <tr className="tot"><td>Total</td><td className="py">{py(panel.payor, panel.total)}</td><td className="n">{amt(v.c, panel.total)}</td></tr>
    </>
  );
}

function Results() {
  const v = useCaseView();
  const update = useDis((s) => s.update);
  const [relOpen, setRelOpen] = useState(false);
  const r = v.result;
  if (!r) return <><Fc4055Notice /><div className="warnbox">{v.engineError ? `The engine could not compute this case: ${v.engineError}` : 'Calculating…'}</div></>;
  const L = v.labels;
  const g = guidelinePanel(r);
  const p = proposedPanel(r);
  const li = r.guideline.lowIncome;
  const n = v.c.children.length;
  const liaApplies = li.payorNet > 0 && li.payorNet < li.threshold && n > 0;
  const liaText = !n || v.c.settings.csMethod !== 'GUIDELINE' ? 'Low-income adjustment: not applicable'
    : liaApplies ? `Low-income adjustment applies: payor net ${money(v.c, li.payorNet)} is below ${money(v.c, li.threshold)}${v.c.settings.displayLowIncomeRange ? `. Range ${money(v.c, li.low)} to ${money(v.c, li.high)}` : ''}${li.applied ? ' (low end applied)' : ''}`
      : `Low-income adjustment: does not apply (payor net ${money(v.c, li.payorNet)}, threshold ${money(v.c, li.threshold)})`;
  const hi = r.guideline.higherEarner;
  const releaser = other(hi);
  const changeG = [r.parties[0].proposed.changeFromGuideline, r.parties[1].proposed.changeFromGuideline];
  const t = v.c.proposed.tactic;
  return (
    <>
      <Fc4055Notice />
      {r.errors.length > 0 && <div className="warnbox">{r.errors.join(' ')}</div>}
      {r.warnings.length > 0 && <div className="notice amb"><span>⚠</span><span>{r.warnings.join(' ')}</span></div>}
      <section className="panel" aria-label="Guideline support">
        <header><h3>Guideline support</h3><span className="sub">{v.c.taxYear} {v.c.mode === 'ANNUAL' ? 'Yearly' : 'Monthly'}</span><span className="sp" /><span className="sub">Nets {v.c.settings.ssOnAdjustedNets ? '(adjusted)' : '(unadjusted)'} · FC 4055 {fcWord(v.fcApplied)} 9/1/2024</span></header>
        <Headline total={g.total} payor={g.payor} v={v} />
        <table className="lines"><tbody><Lines kind="g" panel={g} /></tbody></table>
        <div className="foot">
          <span>{L[0]} net <b>{money(v.c, r.parties[0].netDisposable)}</b></span><span>{L[1]} net <b>{money(v.c, r.parties[1].netDisposable)}</b></span>
          <span>Total <b>{money(v.c, r.parties[0].netDisposable + r.parties[1].netDisposable)}</b></span><span>K <b>{r.guideline.kFactor.toFixed(4)}</b></span>
          <span>Timeshare <b>{Math.round(r.guideline.hPercent * 100)}%</b> with the higher earner ({L[hi]})</span>
        </div>
        {(v.c.settings.displayLowIncomeRange || liaApplies) && <div className="foot" style={{ borderTop: 0, paddingTop: 0 }}>{liaText}</div>}
      </section>
      <section className="panel prop" aria-label="Proposed support">
        <header><h3>Proposed support</h3><span className="sub">{v.c.taxYear} {v.c.mode === 'ANNUAL' ? 'Yearly' : 'Monthly'}, tactic {t}{tacticUsesSub(t) ? subLetter(v.c.proposed.sub) : ''}</span><span className="sp" /><span className="sub">{tacticLong(t, L)}</span></header>
        <Headline total={p.total} payor={p.payor} v={v} />
        <table className="lines"><tbody>
          <Lines kind="p" panel={p} />
          <tr className="hl"><td>Increased net spendable</td><td className="py">{L[0]} {paren(v.c, changeG[0] ?? 0)} · {L[1]} {paren(v.c, changeG[1] ?? 0)}</td><td className="n">{paren(v.c, r.proposed.saving)}</td></tr>
          <tr><td>Exemption releases{v.c.children.length > 0 && <button type="button" className="lnkbtn" aria-expanded={relOpen} onClick={() => setRelOpen(!relOpen)}>{relOpen ? 'Hide children' : 'Choose children'}</button>}</td><td className="py" colSpan={2} style={{ textAlign: 'right' }}>{r.proposed.releases ? `${r.proposed.releases} from ${L[releaser]} to ${L[hi]}` : 'none'}{v.c.proposed.releaseChildren ? ' (selected children)' : ''}{r.proposed.converged ? '' : ' · search did not converge'}</td></tr>
        </tbody></table>
        {relOpen && <div style={{ padding: '6px 0 0' }}><ReleasePicker compact /></div>}
        <div className="acts" style={{ paddingTop: 8 }}>
          <label className="muted" htmlFor="propTac">Tactic</label>
          <select id="propTac" value={t} onChange={(e) => update((x) => { x.proposed.tactic = e.target.value as Tactic; })}>
            {TACTICS.map((x) => <option key={x.id} value={x.id}>{x.id} · {tacticLong(x.id, L)}</option>)}
          </select>
          <span style={{ flex: 1 }} />
          <button type="button" className="btn sm" onClick={() => act('rep:releases')}>Releases table</button>
          <button type="button" className="btn sm" onClick={() => act('addScenario')}>Add snapshot</button>
        </div>
      </section>
      <div className={`setstat${v.changed.length ? ' chg' : ''}`}>
        {v.changed.length
          ? <>● {v.changed.length} {v.changed.length === 1 ? 'setting differs' : 'settings differ'} from defaults: {v.changed.map((x) => x.label).join(', ')} <button type="button" className="lnkbtn" onClick={() => act('settings')}>Review</button></>
          : '● Default case settings'}
      </div>
      <p className="muted" style={{ fontSize: 11.5, marginTop: 10 }}>{r.paymentDescriptions[0]}</p>
    </>
  );
}

function CashFlow() {
  const v = useCaseView();
  const open = useDis((s) => s.ui.cfOpen);
  const setUi = useDis((s) => s.setUi);
  const r = v.result;
  if (!r) return <div className="warnbox">No result.</div>;
  const L = v.labels;
  const P = r.parties;
  const c = v.c;
  const pct = (n: number) => `${n.toFixed(1)}%`;
  const row = (lbl: React.ReactNode, a: string, b: string, cc: string, d: string, cls = '') => <tr className={cls}><td>{lbl}</td><td>{a}</td><td>{b}</td><td className="gs">{cc}</td><td>{d}</td></tr>;
  const hid = open ? 'sub' : 'sub hid';
  return (
    <>
      <table className="cf">
        <thead>
          <tr><th className="l" /><th colSpan={2} className="grp">Guideline</th><th colSpan={2} className="grp">Proposed</th></tr>
          <tr><th className="l" /><th className="pa-t">{L[0]}</th><th className="pb-t">{L[1]}</th><th className="pa-t">{L[0]}</th><th className="pb-t">{L[1]}</th></tr>
        </thead>
        <tbody>
          {row('Support cost or benefit', paren(c, P[0].guideline.paymentCostBenefit), paren(c, P[1].guideline.paymentCostBenefit), paren(c, P[0].proposed.paymentCostBenefit), paren(c, P[1].proposed.paymentCostBenefit))}
          {row('Net spendable income', amt(c, P[0].guideline.netSpendable), amt(c, P[1].guideline.netSpendable), amt(c, P[0].proposed.netSpendable), amt(c, P[1].proposed.netSpendable))}
          {row('Change from guideline', '', '', paren(c, P[0].proposed.changeFromGuideline), paren(c, P[1].proposed.changeFromGuideline))}
          {row('Share of combined spendable', pct(P[0].guideline.percentOfCombinedSpendable), pct(P[1].guideline.percentOfCombinedSpendable), pct(P[0].proposed.percentOfCombinedSpendable), pct(P[1].proposed.percentOfCombinedSpendable))}
          {row('Share of the saving', '', '', r.proposed.saving ? pct(P[0].proposed.percentOfSavingOverGuideline) : '—', r.proposed.saving ? pct(P[1].proposed.percentOfSavingOverGuideline) : '—')}
          {row(<><button type="button" className="tri" style={{ border: 0, background: 'none', fontSize: 9, color: 'var(--muted)', padding: 0, marginRight: 4 }} aria-expanded={open} onClick={() => setUi({ cfOpen: !open })}>{open ? '▼' : '▶'}</button>Total taxes</>, amt(c, P[0].guideline.taxes.total), amt(c, P[1].guideline.taxes.total), amt(c, P[0].proposed.taxes.total), amt(c, P[1].proposed.taxes.total))}
          {row('Federal income tax', paren(c, -P[0].guideline.taxes.federalIncome), paren(c, -P[1].guideline.taxes.federalIncome), paren(c, -P[0].proposed.taxes.federalIncome), paren(c, -P[1].proposed.taxes.federalIncome), hid)}
          {row('California income tax', amt(c, P[0].guideline.taxes.stateIncome), amt(c, P[1].guideline.taxes.stateIncome), amt(c, P[0].proposed.taxes.stateIncome), amt(c, P[1].proposed.taxes.stateIncome), hid)}
          {row('Social Security and Medicare', amt(c, P[0].guideline.taxes.fica + P[0].guideline.taxes.selfEmployment), amt(c, P[1].guideline.taxes.fica + P[1].guideline.taxes.selfEmployment), amt(c, P[0].proposed.taxes.fica + P[0].proposed.taxes.selfEmployment), amt(c, P[1].proposed.taxes.fica + P[1].proposed.taxes.selfEmployment), hid)}
          {row('California SDI', amt(c, P[0].guideline.taxes.sdi), amt(c, P[1].guideline.taxes.sdi), amt(c, P[0].proposed.taxes.sdi), amt(c, P[1].proposed.taxes.sdi), hid)}
          <tr className="tot"><td>Combined net spendable</td><td colSpan={2} style={{ textAlign: 'center' }}>{money(c, r.cashFlow.guideline.combinedNetSpendable)}</td><td colSpan={2} className="gs" style={{ textAlign: 'center' }}>{money(c, r.cashFlow.proposed.combinedNetSpendable)}</td></tr>
          <tr><td>Percent change</td><td colSpan={2} style={{ textAlign: 'center' }}>{r.cashFlow.guideline.percentChange.toFixed(2)}%</td><td colSpan={2} className="gs" style={{ textAlign: 'center' }}>{r.cashFlow.proposed.percentChange.toFixed(2)}%</td></tr>
        </tbody>
      </table>
      <p className="muted" style={{ fontSize: 11.5, marginTop: 10 }}>Costs to the payor are in parentheses. On the federal line a refund shows without parentheses. Percent change compares combined net spendable income with no support. Form lines are in the tax form viewer (W).</p>
    </>
  );
}

function Notes() {
  const v = useCaseView();
  const update = useDis((s) => s.update);
  return (
    <>
      <label className="muted" htmlFor="notesTa" style={{ display: 'block', marginBottom: 6 }}>Case notes, saved with the case (the shared comment field, also printed as comments on the formal report)</label>
      <textarea className="notes" id="notesTa" value={v.c.proposed.comment} onChange={(e) => { const t = e.target.value; update((x) => { x.proposed.comment = t; }); }} />
    </>
  );
}

function Help() {
  const sel = useDis((s) => s.ui.sel);
  const topic = useDis((s) => s.ui.helpTopic);
  const id = topic ?? sel;
  const h = helpFor(id);
  const isRow = rowLabel(id) !== id;
  const title = id === 'glossary' ? 'Glossary' : isRow ? rowLabel(id) : ({ sb343: 'FC 4055 version', lia: 'Low-income adjustment', ssFormula: 'County spousal formula', tactic: 'Tactics', deduct: 'Spousal deductibility', rdp: 'Domestic partners', deferredResidence: 'Deferred sale of home', subsequentPartner: 'Subsequent spouse or partner', historical: 'Historical periods', familySupport: 'Family support', imputed: 'Imputed income' } as Record<string, string>)[id] ?? 'disOS';
  return (
    <>
      <div className="helpcard">
        <h3>{title}</h3>
        {id === 'glossary' ? (
          <dl className="glossary">{GLOSSARY.map(([t, d]) => <div key={t}><dt>{t}</dt><dd>{d}</dd></div>)}</dl>
        ) : (<>
        <div className="row">{isRow ? (SUB_PARENT[id] ? `Detail line of ${rowLabel(SUB_PARENT[id] ?? '')}` : 'Case Data row') : 'Setting'}</div>
        {h ? (<>
          <p>{h[0]}</p>
          {h[1].length > 0 && (<>
            <div className="muted" style={{ font: '500 11px var(--sans)', textTransform: 'uppercase', letterSpacing: '.06em', marginTop: 8 }}>Law</div>
            <ul className="cites">{h[1].map(([t, sec], i) => <li key={i}>{sec ? <a href={LEG(sec)} target="_blank" rel="noopener noreferrer">{t}</a> : t}</li>)}</ul>
          </>)}
        </>) : <p className="muted">Choose a row in Case Data to see its help.</p>}
        {id === 'exempt' && <div className="act"><button type="button" className="btn sm" onClick={() => act('restoreAuto')}>Restore automatic value</button></div>}
        </>)}
      </div>
      <div className="helpcard">
        <h3>More topics</h3>
        <div className="act" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {(['glossary', 'sb343', 'lia', 'ssFormula', 'tactic', 'deduct', 'rdp', 'deferredResidence', 'subsequentPartner', 'historical', 'familySupport', 'imputed'] as const).map((k) => <button key={k} type="button" className="btn sm" onClick={() => act(`r:help:${k}`)}>{({ glossary: 'Glossary', sb343: 'FC 4055 version', lia: 'Low-income adjustment', ssFormula: 'Spousal formula', tactic: 'Tactics', deduct: 'Deductibility', rdp: 'Domestic partners', deferredResidence: 'Deferred home sale', subsequentPartner: 'New partner income', historical: 'Historical periods', familySupport: 'Family support', imputed: 'Imputed income' })[k]}</button>)}
        </div>
      </div>
      <div className="helpcard">
        <h3>Keyboard</h3>
        <div className="row">Single keys work when you are not typing in a field</div>
        <table className="keys"><tbody>
          {KEYS.map(([k, d]) => <tr key={k}><td>{k.split(' ').map((x, i) => (x === '–' ? <span key={i}> – </span> : <kbd key={i}>{x}</kbd>))}</td><td>{d}</td></tr>)}
        </tbody></table>
      </div>
    </>
  );
}
